import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Crown, Plus, Search, Users, DollarSign, Star,
  Trash2, RefreshCw, Pencil, Award, Shield, Gift
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Acronym } from '../components/shared/AcronymTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = [
  { value: '#CD7F32', label: 'Bronze' },
  { value: '#C0C0C0', label: 'Silver' },
  { value: '#FFD700', label: 'Gold' },
  { value: '#E5E4E2', label: 'Platinum' },
  { value: '#B76E79', label: 'Rose Gold' },
  { value: '#1D4ED8', label: 'Sapphire' },
];

const StatCard = ({ icon: Icon, label, value, color, prefix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{prefix}{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function VIPTiersPage() {
  const { format: fc, symbol: currSymbol } = useCurrency();
  const [tiers, setTiers] = useState([]);
  const [members, setMembers] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tiers');
  const [showCreateTier, setShowCreateTier] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [tierForm, setTierForm] = useState({ name: '', min_spend: 0, color: '#FFD700', perks: '', discount_pct: 0, priority_support: false, exclusive_events: false });
  const [memberForm, setMemberForm] = useState({ customer_name: '', customer_email: '', tier_id: '', tier_name: '', total_spend: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [t, m, d] = await Promise.all([
        axios.get(`${API}/api/vip-tiers`),
        axios.get(`${API}/api/vip-tiers/members`),
        axios.get(`${API}/api/vip-tiers/dashboard`),
      ]);
      setTiers(t.data.tiers || []);
      setMembers(m.data.members || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTier = async () => {
    if (!tierForm.name.trim()) { toast.error('Tier name required'); return; }
    try {
      await axios.post(`${API}/api/vip-tiers`, {
        ...tierForm,
        perks: tierForm.perks.split(',').map(p => p.trim()).filter(Boolean)
      });
      toast.success('Tier created!');
      setShowCreateTier(false);
      setTierForm({ name: '', min_spend: 0, color: '#FFD700', perks: '', discount_pct: 0, priority_support: false, exclusive_events: false });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleAddMember = async () => {
    if (!memberForm.customer_name.trim() || !memberForm.tier_id) { toast.error('Name and tier required'); return; }
    const tier = tiers.find(t => t.id === memberForm.tier_id);
    try {
      await axios.post(`${API}/api/vip-tiers/members`, {
        ...memberForm,
        tier_name: tier?.name || ''
      });
      toast.success('Member added!');
      setShowAddMember(false);
      setMemberForm({ customer_name: '', customer_email: '', tier_id: '', tier_name: '', total_spend: 0 });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteTier = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    try { await axios.delete(`${API}/api/vip-tiers/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this member?')) return;
    try { await axios.delete(`${API}/api/vip-tiers/members/${id}`); toast.success('Removed'); fetchData(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="vip-tiers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary"><Acronym term="VIP" /> Tiers<PageInfoTooltip pageKey="vip-tiers" /></h1>
          <p className="text-gray-medium text-sm mt-1">Manage customer tiers, perks, and VIP members</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAddMember(true)} data-testid="add-member-btn"><Users className="h-4 w-4 mr-1" />Add Member</Button>
          <Button onClick={() => setShowCreateTier(true)} data-testid="create-tier-btn"><Plus className="h-4 w-4 mr-1" />New Tier</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Crown} label="Total Tiers" value={dashboard.total_tiers || 0} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Users} label="VIP Members" value={dashboard.total_members || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={DollarSign} label="Total Spend" value={fc(dashboard.total_spend || 0)} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={Star} label="Tier Levels" value={tiers.length} color="bg-purple-50 text-purple-600" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList><TabsTrigger value="tiers">Tiers</TabsTrigger><TabsTrigger value="members">Members ({members.length})</TabsTrigger></TabsList>

        <TabsContent value="tiers" className="mt-4">
          {tiers.length === 0 ? (
            <Card className="bg-white border-gray-border"><CardContent className="text-center py-12 text-gray-medium"><Crown className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No tiers yet. Create your first one!</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tiers.map(tier => (
                <Card key={tier.id} className="bg-white border-gray-border hover:shadow-sm transition-shadow" data-testid={`tier-card-${tier.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: tier.color + '30' }}>
                          <Crown className="h-4 w-4" style={{ color: tier.color }} />
                        </div>
                        <h3 className="font-semibold text-text-primary">{tier.name}</h3>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteTier(tier.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-medium">Min Spend</span><span className="font-medium">${tier.min_spend?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-gray-medium">Discount</span><span className="font-medium">{tier.discount_pct}%</span></div>
                      <div className="flex justify-between"><span className="text-gray-medium">Members</span><span className="font-medium">{tier.member_count || 0}</span></div>
                      <div className="flex gap-1.5 flex-wrap">
                        {tier.priority_support && <Badge className="text-[10px] bg-blue-100 text-blue-700">Priority Support</Badge>}
                        {tier.exclusive_events && <Badge className="text-[10px] bg-purple-100 text-purple-700">Exclusive Events</Badge>}
                      </div>
                      {tier.perks?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">{tier.perks.map((p, i) => <Badge key={i} className="text-[9px] bg-gray-100 text-gray-600">{p}</Badge>)}</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <Card className="bg-white border-gray-border">
            <CardContent className="pt-4">
              {members.length === 0 ? (
                <div className="text-center py-12 text-gray-medium"><Users className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No VIP members yet</p></div>
              ) : (
                <div className="space-y-2">
                  {members.map(m => (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 transition-colors" data-testid={`member-row-${m.id}`}>
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Users className="h-4 w-4 text-amber-600" /></div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-text-primary">{m.customer_name}</span>
                        <p className="text-xs text-gray-medium">{m.customer_email || '—'}</p>
                      </div>
                      <Badge className="text-[10px] bg-amber-100 text-amber-700">{m.tier_name || 'Unassigned'}</Badge>
                      <span className="text-sm font-medium text-emerald-600">${(m.total_spend || 0).toLocaleString()}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => removeMember(m.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Tier Dialog */}
      <Dialog open={showCreateTier} onOpenChange={setShowCreateTier}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New VIP Tier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tier Name *</Label><Input value={tierForm.name} onChange={e => setTierForm(f => ({ ...f, name: e.target.value }))} data-testid="tier-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min Spend ($)</Label><Input type="number" min={0} value={tierForm.min_spend} onChange={e => setTierForm(f => ({ ...f, min_spend: Number(e.target.value) }))} /></div>
              <div><Label>Discount (%)</Label><Input type="number" min={0} max={100} value={tierForm.discount_pct} onChange={e => setTierForm(f => ({ ...f, discount_pct: Number(e.target.value) }))} /></div>
            </div>
            <div><Label>Color</Label>
              <div className="flex gap-2 mt-1">{TIER_COLORS.map(c => (
                <button key={c.value} className={cn("w-8 h-8 rounded-full border-2 transition-all", tierForm.color === c.value ? "border-text-primary scale-110" : "border-transparent")} style={{ backgroundColor: c.value }} onClick={() => setTierForm(f => ({ ...f, color: c.value }))} title={c.label} />
              ))}</div>
            </div>
            <div><Label>Perks (comma-separated)</Label><Input value={tierForm.perks} onChange={e => setTierForm(f => ({ ...f, perks: e.target.value }))} placeholder="Free shipping, Early access, VIP support" /></div>
            <div className="flex items-center justify-between"><Label>Priority Support</Label><Switch checked={tierForm.priority_support} onCheckedChange={v => setTierForm(f => ({ ...f, priority_support: v }))} /></div>
            <div className="flex items-center justify-between"><Label>Exclusive Events</Label><Switch checked={tierForm.exclusive_events} onCheckedChange={v => setTierForm(f => ({ ...f, exclusive_events: v }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateTier(false)}>Cancel</Button><Button onClick={handleCreateTier} data-testid="save-tier-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add VIP Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Customer Name *</Label><Input value={memberForm.customer_name} onChange={e => setMemberForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="member-name" /></div>
            <div><Label>Email</Label><Input value={memberForm.customer_email} onChange={e => setMemberForm(f => ({ ...f, customer_email: e.target.value }))} /></div>
            <div><Label>Tier *</Label>
              <Select value={memberForm.tier_id} onValueChange={v => setMemberForm(f => ({ ...f, tier_id: v }))}>
                <SelectTrigger data-testid="member-tier-select"><SelectValue placeholder="Select tier" /></SelectTrigger>
                <SelectContent>{tiers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Total Spend ($)</Label><Input type="number" min={0} value={memberForm.total_spend} onChange={e => setMemberForm(f => ({ ...f, total_spend: Number(e.target.value) }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button><Button onClick={handleAddMember} data-testid="save-member-btn">Add Member</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
